import type {
  AuthenticationResponseJSON,
  PublicKeyCredentialCreationOptionsJSON,
  PublicKeyCredentialRequestOptionsJSON,
  RegistrationResponseJSON,
} from '@simplewebauthn/browser'
import type {
  ActivityFeedResponse,
  ActivitySummary,
  AdvancedFinanceReport,
  ApiErrorBody,
  AppointmentInput,
  AppointmentStatusUpdate,
  AppointmentWithDetails,
  CatalogItem,
  CatalogItemInput,
  Car,
  CarInput,
  ChangePasswordInput,
  Client,
  ClientInput,
  ClientStatement,
  CurrentStaffAccount,
  DashboardSummary,
  Expense,
  ExpenseCategory,
  ExpenseDetail,
  ExpenseInput,
  ExpenseListItem,
  ExpenseSyncResult,
  FinanceReport,
  GarageDocument,
  Inspection,
  InspectionOverviewAngle,
  InspectionTemplateNode,
  InspectionTemplateNodeInput,
  InspectionTemplateSection,
  InspectionUpdate,
  JobcardAction,
  JobcardAppointment,
  JobcardCreate,
  JobcardDetail,
  JobcardListItem,
  JobcardStatus,
  JobcardUpdate,
  JobcardTimeline,
  LineKind,
  LoginInput,
  OperatingCost,
  OperatingCostInput,
  OperatingCostListItem,
  OperatingCostType,
  OtherIncome,
  OtherIncomeInput,
  OtherIncomeKind,
  OtherIncomeListItem,
  Paginated,
  PaymentMethod,
  SearchResponse,
  Settings,
  SettingsUpdate,
  StaffAccount,
  StaffAccountCreate,
  StaffAccountUpdate,
  StaffAccountWithTempPassword,
  StaffPasskey,
  StaffEvent,
  StaffEventInput,
  StaffEventWithWorker,
  StaffTransaction,
  StaffTransactionInput,
  StaffPaymentInput,
  StorageAllocation,
  StorageAllocationInput,
  StorageNode,
  StorageNodeInput,
  StorageRack,
  TimelineEntryInput,
  TimelineEntryPatch,
  UpdateStatus,
  VatReturnSummary,
  Worker,
  WorkerDetail,
  WorkerInput,
  WorkerListItem,
  Department,
  DepartmentInput,
  DepartmentWithCount,
  Supplier,
  SupplierInput,
} from '@garage/shared'

/**
 * The typed API client. Every page talks to the server through this module and
 * nowhere else, so a route change is a one-file edit.
 */

/** Thrown for any non-2xx response, carrying the server's error envelope. */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly body: ApiErrorBody,
  ) {
    super(body.error.message)
    this.name = 'ApiError'
  }

  /** Field-level validation messages, keyed by field path, when present. */
  get fieldErrors(): Record<string, string> {
    const details = this.body.error.details
    if (!details || typeof details !== 'object') return {}
    const issues = (details as { issues?: Array<{ path: (string | number)[]; message: string }> })
      .issues
    if (!Array.isArray(issues)) return {}
    const out: Record<string, string> = {}
    for (const issue of issues) {
      const key = issue.path.join('.')
      if (key && !(key in out)) out[key] = issue.message
    }
    return out
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  /*
   * Only declare a JSON body when there IS one.
   *
   * Fastify parses the body of any request that announces a content type, and
   * rejects an empty one:
   *
   *     400 Body cannot be empty when content-type is set to 'application/json'
   *
   * Every DELETE, and every POST that carries no payload — regenerating a
   * customer link, requesting an update — sent the header anyway and was
   * refused before reaching its route. The header describes a body; with no
   * body there is nothing to describe.
   *
   * FormData sets its own content type, including the multipart boundary,
   * which cannot be written by hand.
   */
  const hasJsonBody = init?.body !== undefined && init.body !== null && !(init.body instanceof FormData)

  const response = await fetch(`/api${path}`, {
    ...init,
    headers: {
      ...(hasJsonBody ? { 'Content-Type': 'application/json' } : {}),
      ...init?.headers,
    },
  })

  if (!response.ok) {
    let body: ApiErrorBody
    try {
      const raw = (await response.json()) as
        | ApiErrorBody
        | { code?: string; message?: string; error?: string }
      /*
       * Prefer our documented envelope, but fall back to a flat
       * `{ code, message }` shape. A mis-registered error handler once made the
       * server emit the flat form, and reading `body.error.message` off it gave
       * `undefined` — so every failure in the app surfaced as an EMPTY message.
       * Never let the UI show nothing just because the shape shifted.
       */
      const enveloped = (raw as ApiErrorBody).error
      body =
        enveloped && typeof enveloped === 'object'
          ? (raw as ApiErrorBody)
          : {
              error: {
                code: ((raw as { code?: string }).code as ApiErrorBody['error']['code']) ??
                  'internal_error',
                message:
                  (raw as { message?: string }).message ||
                  response.statusText ||
                  'Request failed',
              },
            }
    } catch {
      body = { error: { code: 'internal_error', message: response.statusText || 'Request failed' } }
    }
    throw new ApiError(response.status, body)
  }

  if (response.status === 204) return undefined as T
  return (await response.json()) as T
}

const qs = (params: Record<string, string | number | boolean | undefined | null>): string => {
  const search = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null && value !== '') search.set(key, String(value))
  }
  const text = search.toString()
  return text ? `?${text}` : ''
}

export interface ClientDetail extends Client {
  cars: Car[]
  jobcards: JobcardListItem[]
}

export const api = {
  dashboard: () => request<DashboardSummary>('/dashboard'),

  auth: {
    /** 401 (thrown as ApiError) means no session — that IS the "logged out" answer. */
    me: () => request<CurrentStaffAccount>('/auth/me'),
    login: (input: LoginInput) =>
      request<CurrentStaffAccount>('/auth/login', { method: 'POST', body: JSON.stringify(input) }),
    logout: () => request<void>('/auth/logout', { method: 'POST' }),
    changePassword: (input: ChangePasswordInput) =>
      request<void>('/auth/change-password', { method: 'POST', body: JSON.stringify(input) }),

    /** The signed-in account's own passkeys (Face ID / fingerprint / Windows Hello). */
    listPasskeys: () => request<StaffPasskey[]>('/auth/passkeys'),
    removePasskey: (id: string) => request<void>(`/auth/passkeys/${id}`, { method: 'DELETE' }),
    passkeyRegisterOptions: () =>
      request<PublicKeyCredentialCreationOptionsJSON>('/auth/passkeys/register/options', { method: 'POST' }),
    passkeyRegisterVerify: (input: { deviceName: string; response: RegistrationResponseJSON }) =>
      request<void>('/auth/passkeys/register/verify', { method: 'POST', body: JSON.stringify(input) }),
    /** Public: no session needed to sign in with a passkey. */
    passkeyLoginOptions: () =>
      request<PublicKeyCredentialRequestOptionsJSON>('/auth/passkeys/login/options', { method: 'POST' }),
    passkeyLoginVerify: (response: AuthenticationResponseJSON) =>
      request<CurrentStaffAccount>('/auth/passkeys/login/verify', {
        method: 'POST',
        body: JSON.stringify({ response }),
      }),
  },

  /** Who did what — the per-record summary is available to anyone who can
   *  already see that record; the system-wide feed is admin-only. */
  activity: {
    summary: (entityType: string, entityId: string) =>
      request<ActivitySummary>(`/activity${qs({ entityType, entityId })}`),
    feed: (params: { page?: number; pageSize?: number; entityType?: string; staffAccountId?: string } = {}) =>
      request<ActivityFeedResponse>(`/activity/feed${qs(params)}`),
  },

  /** Admin-only: who can sign in, and as what role. */
  staffAccounts: {
    list: () => request<{ items: StaffAccount[] }>('/staff-accounts'),
    create: (input: StaffAccountCreate) =>
      request<StaffAccountWithTempPassword>('/staff-accounts', { method: 'POST', body: JSON.stringify(input) }),
    update: (id: string, input: StaffAccountUpdate) =>
      request<StaffAccount>(`/staff-accounts/${id}`, { method: 'PATCH', body: JSON.stringify(input) }),
    /** New temporary password, shown once; also signs the account out everywhere. */
    resetPassword: (id: string) =>
      request<StaffAccountWithTempPassword>(`/staff-accounts/${id}/reset-password`, { method: 'POST' }),
    /** Removes the account for good, distinct from disabling it. */
    remove: (id: string) => request<void>(`/staff-accounts/${id}`, { method: 'DELETE' }),
  },

  search: (q: string) => request<SearchResponse>(`/search${qs({ q })}`),

  /** Cash-basis finance report over the last `months` whole months. */
  finance: (months = 12) => request<FinanceReport>(`/finance${qs({ months })}`),

  /** Advanced P&L report over the last `months` whole months (advanced mode only). */
  financeAdvanced: (months = 12) => request<AdvancedFinanceReport>(`/finance/advanced${qs({ months })}`),

  clients: {
    list: (params: { q?: string; page?: number; pageSize?: number } = {}) =>
      request<Paginated<Client>>(`/clients${qs(params)}`),
    get: (id: string) => request<ClientDetail>(`/clients/${id}`),
    statement: (id: string) => request<ClientStatement>(`/clients/${id}/statement`),
    /** Direct link: a zip of every unpaid invoice PDF, downloaded by the browser. */
    unpaidInvoicesUrl: (id: string) => `/api/clients/${id}/unpaid-invoices.zip`,
    create: (input: ClientInput) =>
      request<Client>('/clients', { method: 'POST', body: JSON.stringify(input) }),
    update: (id: string, input: ClientInput) =>
      request<Client>(`/clients/${id}`, { method: 'PATCH', body: JSON.stringify(input) }),
    remove: (id: string) => request<void>(`/clients/${id}`, { method: 'DELETE' }),
  },

  cars: {
    list: (params: { clientId?: string; q?: string } = {}) => request<Car[]>(`/cars${qs(params)}`),
    create: (input: CarInput) =>
      request<Car>('/cars', { method: 'POST', body: JSON.stringify(input) }),
    update: (id: string, input: CarInput) =>
      request<Car>(`/cars/${id}`, { method: 'PATCH', body: JSON.stringify(input) }),
    remove: (id: string) => request<void>(`/cars/${id}`, { method: 'DELETE' }),
  },

  jobcards: {
    list: (
      params: {
        status?: JobcardStatus | 'open' | 'closed' | 'all'
        clientId?: string
        q?: string
        page?: number
        pageSize?: number
      } = {},
    ) => request<Paginated<JobcardListItem>>(`/jobcards${qs(params)}`),
    get: (id: string) => request<JobcardDetail>(`/jobcards/${id}`),
    create: (input: JobcardCreate) =>
      request<JobcardDetail>('/jobcards', { method: 'POST', body: JSON.stringify(input) }),
    update: (id: string, input: JobcardUpdate) =>
      request<JobcardDetail>(`/jobcards/${id}`, { method: 'PATCH', body: JSON.stringify(input) }),
    /** Performs the action; the server rejects an illegal one with 409. */
    transition: (id: string, action: JobcardAction, paymentMethod?: PaymentMethod) =>
      request<JobcardDetail>(`/jobcards/${id}/transition`, {
        method: 'POST',
        body: JSON.stringify({ action, paymentMethod }),
      }),
    /** Regenerate the public token, invalidating the old link. */
    regenerateLink: (id: string) =>
      request<{ publicToken: string }>(`/jobcards/${id}/regenerate-link`, {
        method: 'POST',
      }),
    remove: (id: string) => request<void>(`/jobcards/${id}`, { method: 'DELETE' }),
    /** Cars booked in over a date range, for the calendar's workload view. Both bounds inclusive, "YYYY-MM-DD". */
    appointments: (params: { from: string; to: string }) =>
      request<{ items: JobcardAppointment[] }>(`/jobcards/appointments${qs(params)}`),

    timeline: {
      get: (jobcardId: string) => request<JobcardTimeline>(`/jobcards/${jobcardId}/timeline`),
      add: (jobcardId: string, input: TimelineEntryInput) =>
        request<{ id: string }>(`/jobcards/${jobcardId}/timeline`, {
          method: 'POST',
          body: JSON.stringify(input),
        }),
      update: (jobcardId: string, entryId: string, patch: TimelineEntryPatch) =>
        request<{ id: string }>(`/jobcards/${jobcardId}/timeline/${entryId}`, {
          method: 'PATCH',
          body: JSON.stringify(patch),
        }),
      remove: (jobcardId: string, entryId: string) =>
        request<void>(`/jobcards/${jobcardId}/timeline/${entryId}`, { method: 'DELETE' }),
    },
  },

  documents: {
    /** Issues a document AND applies the status transition that goes with it. */
    create: (jobcardId: string, type: 'quotation' | 'invoice', backTerms?: string) =>
      request<GarageDocument>(`/jobcards/${jobcardId}/documents`, {
        method: 'POST',
        body: JSON.stringify({ type, ...(backTerms ? { backTerms } : {}) }),
      }),
    get: (id: string) => request<GarageDocument>(`/documents/${id}`),
    /** Direct link; used as an href rather than fetched. */
    pdfUrl: (id: string) => `/api/documents/${id}/pdf`,
  },

  catalog: {
    list: (params: { kind?: LineKind; q?: string; page?: number; pageSize?: number } = {}) =>
      request<Paginated<CatalogItem>>(`/catalog${qs(params)}`),
    /** Autocomplete for the jobcard line editor. Ranked, capped at 10. */
    suggest: (kind: LineKind, q: string) =>
      request<CatalogItem[]>(`/catalog/suggest${qs({ kind, q })}`),
    create: (input: CatalogItemInput) =>
      request<CatalogItem>('/catalog', { method: 'POST', body: JSON.stringify(input) }),
    update: (id: string, input: CatalogItemInput) =>
      request<CatalogItem>(`/catalog/${id}`, { method: 'PATCH', body: JSON.stringify(input) }),
    remove: (id: string) => request<void>(`/catalog/${id}`, { method: 'DELETE' }),
  },

  settings: {
    get: () => request<Settings>('/settings'),
    update: (input: SettingsUpdate) =>
      request<Settings>('/settings', { method: 'PATCH', body: JSON.stringify(input) }),
    uploadLogo: (file: File) => {
      const form = new FormData()
      form.append('logo', file)
      return request<Settings>('/settings/logo', { method: 'POST', body: form })
    },
    logoUrl: () => '/api/settings/logo',
    uploadExcelImport: (category: ExpenseCategory, file: File) => {
      const form = new FormData()
      form.append('file', file)
      return request<Settings>(`/settings/excel-import/${category}`, { method: 'POST', body: form })
    },
  },

  /**
   * Which build is running. Shown in the sidebar so "is my update live?" can
   * be answered from the app itself rather than from the deploy console.
   */
  version: () => request<{ commit: string; builtAt: string }>('/version'),

  update: {
    status: () => request<UpdateStatus>('/update/status'),
    /** Records the request; the host helper does the work minutes later. */
    start: () => request<UpdateStatus>('/update/start', { method: 'POST' }),
  },

  /** Href, not a fetch: the browser streams the zip straight to disk. */
  backupUrl: () => '/api/backup',

  workers: {
    list: (params: { q?: string } = {}) => request<{ items: WorkerListItem[] }>(`/workers${qs(params)}`),
    get: (id: string) => request<WorkerDetail>(`/workers/${id}`),
    create: (input: WorkerInput) =>
      request<Worker>('/workers', { method: 'POST', body: JSON.stringify(input) }),
    update: (id: string, input: WorkerInput) =>
      request<Worker>(`/workers/${id}`, { method: 'PATCH', body: JSON.stringify(input) }),
    remove: (id: string) => request<void>(`/workers/${id}`, { method: 'DELETE' }),

    addTransaction: (workerId: string, input: StaffTransactionInput) =>
      request<StaffTransaction>(`/workers/${workerId}/transactions`, {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    updateTransaction: (workerId: string, txId: string, input: StaffTransactionInput) =>
      request<StaffTransaction>(`/workers/${workerId}/transactions/${txId}`, {
        method: 'PATCH',
        body: JSON.stringify(input),
      }),
    removeTransaction: (workerId: string, txId: string) =>
      request<void>(`/workers/${workerId}/transactions/${txId}`, { method: 'DELETE' }),

    addPayment: (workerId: string, txId: string, input: StaffPaymentInput) =>
      request<StaffTransaction>(`/workers/${workerId}/transactions/${txId}/payments`, {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    removePayment: (workerId: string, txId: string, paymentId: string) =>
      request<void>(`/workers/${workerId}/transactions/${txId}/payments/${paymentId}`, { method: 'DELETE' }),
  },

  departments: {
    list: () => request<{ items: DepartmentWithCount[] }>('/departments'),
    create: (input: DepartmentInput) =>
      request<Department>('/departments', { method: 'POST', body: JSON.stringify(input) }),
    update: (id: string, input: DepartmentInput) =>
      request<Department>(`/departments/${id}`, { method: 'PATCH', body: JSON.stringify(input) }),
    remove: (id: string) => request<void>(`/departments/${id}`, { method: 'DELETE' }),
  },

  suppliers: {
    list: (params: { q?: string } = {}) => request<{ items: Supplier[] }>(`/suppliers${qs(params)}`),
    get: (id: string) => request<Supplier>(`/suppliers/${id}`),
    create: (input: SupplierInput) =>
      request<Supplier>('/suppliers', { method: 'POST', body: JSON.stringify(input) }),
    update: (id: string, input: SupplierInput) =>
      request<Supplier>(`/suppliers/${id}`, { method: 'PATCH', body: JSON.stringify(input) }),
    remove: (id: string) => request<void>(`/suppliers/${id}`, { method: 'DELETE' }),
  },

  staffEvents: {
    /** Both bounds inclusive, "YYYY-MM-DD". */
    list: (params: { from: string; to: string }) =>
      request<{ items: StaffEventWithWorker[] }>(`/staff-events${qs(params)}`),
    create: (input: StaffEventInput) =>
      request<StaffEvent>('/staff-events', { method: 'POST', body: JSON.stringify(input) }),
    update: (id: string, input: StaffEventInput) =>
      request<StaffEvent>(`/staff-events/${id}`, { method: 'PATCH', body: JSON.stringify(input) }),
    remove: (id: string) => request<void>(`/staff-events/${id}`, { method: 'DELETE' }),
  },

  appointments: {
    /** Both bounds inclusive, "YYYY-MM-DD". */
    list: (params: { from: string; to: string }) =>
      request<{ items: AppointmentWithDetails[] }>(`/appointments${qs(params)}`),
    get: (id: string) => request<AppointmentWithDetails>(`/appointments/${id}`),
    create: (input: AppointmentInput) =>
      request<AppointmentWithDetails>('/appointments', { method: 'POST', body: JSON.stringify(input) }),
    update: (id: string, input: AppointmentInput) =>
      request<AppointmentWithDetails>(`/appointments/${id}`, { method: 'PATCH', body: JSON.stringify(input) }),
    setStatus: (id: string, input: AppointmentStatusUpdate) =>
      request<AppointmentWithDetails>(`/appointments/${id}/status`, { method: 'PATCH', body: JSON.stringify(input) }),
    remove: (id: string) => request<void>(`/appointments/${id}`, { method: 'DELETE' }),
  },

  expenses: {
    list: (params: { category?: ExpenseCategory; q?: string; from?: string; to?: string; trn?: 'with' | 'without' | 'none'; page?: number; pageSize?: number } = {}) =>
      request<Paginated<ExpenseListItem>>(`/expenses${qs(params)}`),
    get: (id: string) => request<ExpenseDetail>(`/expenses/${id}`),
    create: (input: ExpenseInput) =>
      request<ExpenseDetail>('/expenses', { method: 'POST', body: JSON.stringify(input) }),
    update: (id: string, input: ExpenseInput) =>
      request<ExpenseDetail>(`/expenses/${id}`, { method: 'PATCH', body: JSON.stringify(input) }),
    remove: (id: string) => request<void>(`/expenses/${id}`, { method: 'DELETE' }),
    /** Both bounds inclusive, "YYYY-MM-DD". `trn` omitted means both TRN and no-TRN invoices. */
    vatSummary: (params: { from: string; to: string; trn?: 'with' | 'without' | 'none' }) =>
      request<VatReturnSummary>(`/expenses/vat-summary${qs(params)}`),
    /**
     * Imports new invoices from the Excel file configured in Settings for
     * this category, dated within [from, to] (both "YYYY-MM-DD", inclusive)
     * — a row outside that range is left alone even if present in the sheet.
     */
    sync: (category: ExpenseCategory, range: { from: string; to: string }) =>
      request<ExpenseSyncResult>(`/expenses/sync/${category}${qs(range)}`, { method: 'POST' }),
  },

  operatingCosts: {
    list: (params: { type?: OperatingCostType; q?: string; from?: string; to?: string; page?: number; pageSize?: number } = {}) =>
      request<Paginated<OperatingCostListItem>>(`/operating-costs${qs(params)}`),
    get: (id: string) => request<OperatingCost>(`/operating-costs/${id}`),
    create: (input: OperatingCostInput) =>
      request<OperatingCost>('/operating-costs', { method: 'POST', body: JSON.stringify(input) }),
    update: (id: string, input: OperatingCostInput) =>
      request<OperatingCost>(`/operating-costs/${id}`, { method: 'PATCH', body: JSON.stringify(input) }),
    remove: (id: string) => request<void>(`/operating-costs/${id}`, { method: 'DELETE' }),
  },

  otherIncome: {
    list: (params: { kind?: OtherIncomeKind; q?: string; from?: string; to?: string; page?: number; pageSize?: number } = {}) =>
      request<Paginated<OtherIncomeListItem>>(`/other-income${qs(params)}`),
    get: (id: string) => request<OtherIncome>(`/other-income/${id}`),
    create: (input: OtherIncomeInput) =>
      request<OtherIncome>('/other-income', { method: 'POST', body: JSON.stringify(input) }),
    update: (id: string, input: OtherIncomeInput) =>
      request<OtherIncome>(`/other-income/${id}`, { method: 'PATCH', body: JSON.stringify(input) }),
    remove: (id: string) => request<void>(`/other-income/${id}`, { method: 'DELETE' }),
  },

  storage: {
    tree: () => request<{ items: StorageRack[] }>('/storage/tree'),
    createNode: (parentId: string | null, input: StorageNodeInput) =>
      request<StorageNode>('/storage/nodes', { method: 'POST', body: JSON.stringify({ parentId, ...input }) }),
    updateNode: (id: string, input: StorageNodeInput) =>
      request<StorageNode>(`/storage/nodes/${id}`, { method: 'PATCH', body: JSON.stringify(input) }),
    removeNode: (id: string) => request<void>(`/storage/nodes/${id}`, { method: 'DELETE' }),

    createAllocation: (levelId: string, input: StorageAllocationInput) =>
      request<StorageAllocation>(`/storage/levels/${levelId}/allocations`, {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    updateAllocation: (id: string, input: StorageAllocationInput) =>
      request<StorageAllocation>(`/storage/allocations/${id}`, { method: 'PATCH', body: JSON.stringify(input) }),
    removeAllocation: (id: string) => request<void>(`/storage/allocations/${id}`, { method: 'DELETE' }),
    addItemPhoto: (id: string, file: File) => {
      const form = new FormData()
      form.append('photo', file)
      return request<StorageAllocation>(`/storage/allocations/${id}/photos`, { method: 'POST', body: form })
    },
    removeItemPhoto: (id: string, photoId: string) =>
      request<StorageAllocation>(`/storage/allocations/${id}/photos/${photoId}`, { method: 'DELETE' }),
    itemPhotoUrl: (id: string, photoId: string, width?: 128 | 320 | 1600) => {
      const url = `/api/storage/allocations/${id}/photos/${photoId}`
      return width ? `${url}?w=${width}` : url
    },
  },

  inspections: {
    get: (jobcardId: string) => request<Inspection>(`/jobcards/${jobcardId}/inspection`),
    save: (jobcardId: string, input: InspectionUpdate) =>
      request<Inspection>(`/jobcards/${jobcardId}/inspection`, { method: 'PUT', body: JSON.stringify(input) }),

    uploadItemPhoto: (jobcardId: string, itemId: string, file: File) => {
      const form = new FormData()
      form.append('photo', file)
      return request<Inspection>(`/jobcards/${jobcardId}/inspection/items/${itemId}/photo`, {
        method: 'POST',
        body: form,
      })
    },
    removeItemPhoto: (jobcardId: string, itemId: string) =>
      request<Inspection>(`/jobcards/${jobcardId}/inspection/items/${itemId}/photo`, { method: 'DELETE' }),
    itemPhotoUrl: (jobcardId: string, itemId: string, width?: 128 | 320 | 1600, version?: number) => {
      const url = `/api/jobcards/${jobcardId}/inspection/items/${itemId}/photo`
      const params = new URLSearchParams()
      if (width) params.append('w', String(width))
      if (version) params.append('v', String(version))
      const query = params.toString()
      return query ? `${url}?${query}` : url
    },

    uploadOverviewPhoto: (jobcardId: string, angle: InspectionOverviewAngle, file: File) => {
      const form = new FormData()
      form.append('photo', file)
      return request<Inspection>(`/jobcards/${jobcardId}/inspection/overview-photos/${angle}`, {
        method: 'POST',
        body: form,
      })
    },
    removeOverviewPhoto: (jobcardId: string, angle: InspectionOverviewAngle) =>
      request<Inspection>(`/jobcards/${jobcardId}/inspection/overview-photos/${angle}`, { method: 'DELETE' }),
    overviewPhotoUrl: (jobcardId: string, angle: InspectionOverviewAngle, width?: 128 | 320 | 1600, version?: number) => {
      const url = `/api/jobcards/${jobcardId}/inspection/overview-photos/${angle}`
      const params = new URLSearchParams()
      if (width) params.append('w', String(width))
      if (version) params.append('v', String(version))
      const query = params.toString()
      return query ? `${url}?${query}` : url
    },
  },

  inspectionTemplate: {
    tree: () => request<{ items: InspectionTemplateSection[] }>('/inspection-template'),
    createNode: (parentId: string | null, input: InspectionTemplateNodeInput) =>
      request<InspectionTemplateNode>('/inspection-template/nodes', {
        method: 'POST',
        body: JSON.stringify({ parentId, ...input }),
      }),
    updateNode: (id: string, input: InspectionTemplateNodeInput) =>
      request<InspectionTemplateNode>(`/inspection-template/nodes/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(input),
      }),
    removeNode: (id: string) => request<void>(`/inspection-template/nodes/${id}`, { method: 'DELETE' }),
  },
}
